import { useEffect, useMemo, useRef, useState } from 'react';
import { getStorageJSON, setStorageJSON } from '../utils/userStorage';

const STORAGE_KEY = 'searchCampaignFilterIds';
const EVENT_NAME = 'search-campaign-filter-updated';

const normalizeIds = ids =>
  Array.isArray(ids) ? Array.from(new Set(ids.filter(Boolean))) : [];

const areSameIds = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

const reconcileSelection = (selectedIds, availableIds) => {
  const available = normalizeIds(availableIds);
  const availableSet = new Set(available);
  return normalizeIds(selectedIds).filter(id => availableSet.has(id));
};

const ensureSelectionOrAll = (selectedIds, availableIds) => {
  const available = normalizeIds(availableIds);
  if (available.length === 0) {
    return [];
  }
  const reconciled = reconcileSelection(selectedIds, available);
  return reconciled.length > 0 ? reconciled : available;
};

export const useSyncedCampaignFilter = (availableCampaignIds, scope = 'global') => {
  const availableSourceKey = useMemo(
    () =>
      Array.isArray(availableCampaignIds)
        ? availableCampaignIds.filter(Boolean).join(',')
        : '',
    [availableCampaignIds],
  );
  const storageScopedKey = useMemo(
    () => `${STORAGE_KEY}:${scope}`,
    [scope],
  );
  const availableIds = useMemo(
    () =>
      normalizeIds(availableSourceKey ? availableSourceKey.split(',') : []),
    [availableSourceKey],
  );
  const availableKey = useMemo(() => availableIds.join(','), [availableIds]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const initializedRef = useRef(false);
  const defaultToAllRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      const stored = getStorageJSON(storageScopedKey, null);
      if (Array.isArray(stored)) {
        const next = ensureSelectionOrAll(stored, availableIds);
        defaultToAllRef.current = next.length === availableIds.length;
        setSelectedCampaignIds(next);
      } else {
        defaultToAllRef.current = true;
        setSelectedCampaignIds(availableIds);
      }
      setIsHydrated(true);
      return;
    }
    setSelectedCampaignIds(prev => {
      if (defaultToAllRef.current && availableIds.length > 0) {
        return areSameIds(prev, availableIds) ? prev : availableIds;
      }
      const stored = getStorageJSON(storageScopedKey, null);
      const baseSelection = Array.isArray(stored) ? stored : prev;
      const reconciled = ensureSelectionOrAll(baseSelection, availableIds);
      return areSameIds(prev, reconciled) ? prev : reconciled;
    });
  }, [storageScopedKey, availableKey, availableIds]);

  useEffect(() => {
    if (!initializedRef.current || !isHydrated) {
      return;
    }
    // Avoid persisting an empty selection while campaigns are still loading.
    if (availableIds.length === 0) {
      return;
    }
    if (
      defaultToAllRef.current &&
      availableIds.length > 0 &&
      selectedCampaignIds.length === 0
    ) {
      return;
    }
    if (selectedCampaignIds.length > 0) {
      defaultToAllRef.current = false;
    }
    setStorageJSON(storageScopedKey, selectedCampaignIds);
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        detail: { storageScopedKey, selectedCampaignIds },
      }),
    );
  }, [
    storageScopedKey,
    selectedCampaignIds,
    availableKey,
    availableIds,
    isHydrated,
  ]);

  useEffect(() => {
    const syncFromEvent = event => {
      if (event?.detail?.storageScopedKey !== storageScopedKey) {
        return;
      }
      defaultToAllRef.current = false;
      setSelectedCampaignIds(prev => {
        const next = ensureSelectionOrAll(
          event?.detail?.selectedCampaignIds,
          availableIds,
        );
        return areSameIds(prev, next) ? prev : next;
      });
    };

    const syncFromStorage = event => {
      if (event?.key !== storageScopedKey || !event?.newValue) {
        return;
      }
      try {
        const parsed = JSON.parse(event.newValue);
        defaultToAllRef.current = false;
        setSelectedCampaignIds(prev => {
          const next = ensureSelectionOrAll(parsed, availableIds);
          return areSameIds(prev, next) ? prev : next;
        });
      } catch (e) {
        // ignore malformed storage values
      }
    };

    window.addEventListener(EVENT_NAME, syncFromEvent);
    window.addEventListener('storage', syncFromStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, syncFromEvent);
      window.removeEventListener('storage', syncFromStorage);
    };
  }, [storageScopedKey, availableKey, availableIds]);

  return [selectedCampaignIds, setSelectedCampaignIds];
};
